import React, { useState, useRef, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    makeStyles,
    TextField,
    Typography,
    withStyles
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import TinyMce from '../TinyMce/TinyMce';
import { toastr } from 'react-redux-toastr';
import LoadingScreen from '../loadingScreen';
import PropTypes from 'prop-types';
import { modalsButtonStyles } from '../../../../collums-constants/styles/stylesheets/buttonsStyles';
import TemplatesApi from '../../../../api/templatesApi';
import InvoiceApi from '../../../api/InvoiceApi';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { CREATE_NEW_TEMPLATE_OPTION } from '../../../../collums-constants';
import MergeTagsSidebar from '../../../../components/customer/forms/FormModal/MergeTagsSidebar';

const styles = {
    templateAutocomplete: {
        marginBottom: 10,
        '& .MuiTextField-root': {
            width: '100%'
        },
        '& .MuiAutocomplete-input': {
            paddingTop: '2px !important'
        },
        '& .MuiAutocomplete-inputRoot': {
            padding: '5px !important'
        }
    },
    actionButtonCompleteForm: {
        fontSize: 14,
        fontWeight: 500,
        textTransform: 'none',
        backgroundColor: '#B41678',
        color: 'white',
        '&:hover': {
            backgroundColor: '#981468'
        }
    }
};

const SendInvoiceEmailModal = ({ classes, onClose, onSend, customer, invoiceCode, invoiceId }) => {
    const defaultInvoiceEmailContent = `<p style="text-align: center;">Hi ${customer?.firstName ||
        ''}</p><p style="text-align: center;">Please find your invoice attached.</p>`;
    const [isLoading, setIsLoading] = useState(false);
    const [subject, setSubject] = useState(`Invoice ${invoiceCode || ''}`);
    const [emailTo, setEmailTo] = useState(customer?.email || '');
    const [emailCc, setEmailCc] = useState('');
    const [content, setContent] = useState(defaultInvoiceEmailContent);
    const editorRef = useRef(null);
    const globalStyles = makeStyles(modalsButtonStyles)();
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState();
    const [templateIndex, setTemplateIndex] = useState('');
    const [invoiceDetails, setInvoiceDetails] = useState('');

    const loadTemplates = () => {
        TemplatesApi.getTemplates({
            type: 'email'
        }).then(res => setEmailTemplates(res.data));
    };

    const loadEmailPreview = async () => {
        if (!invoiceId) return;
        setIsLoading(true);
        try {
            const preview = await InvoiceApi.getEmailPreview(invoiceId);
            if (preview?.content) {
                setContent(preview.content);
                if (editorRef?.current) {
                    editorRef.current.value = preview.content;
                }
            }
            if (preview?.subject) {
                setSubject(preview.subject);
            }
            if (preview?.invoiceDetails) {
                setInvoiceDetails(preview.invoiceDetails);
            }
        } catch (err) {
            console.error('Failed to load email preview:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
        loadEmailPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedTemplate) return;
        emailTemplates.forEach((template, index) => {
            if (template.name === selectedTemplate.name) {
                setTemplateIndex(index);
            }
        });
        //eslint-disable-next-line
    }, [emailTemplates]);

    const defaultConfig = {
        selector: '.tinymce',
        plugins: ['lists', 'code', 'image', 'link', 'media', 'table'],
        toolbar:
            'undo redo | ' +
            'styles | ' +
            'bold italic strikethrough forecolor backcolor | ' +
            'link image media | ' +
            'table | ' +
            'indent align bullist numlist | ' +
            'code removeformat',
        table_toolbar:
            'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
        toolbar_mode: 'wrap',
        fixed_toolbar_container: '.toolbar',
        menubar: true,
        inline: false,
        auto_focus: 'editor-1',
        object_resizing: false,
        placeholder: 'Write here...',
        images_file_types: 'jpeg,jpg,png,gif',
        branding: false,
        browser_spellcheck: true,
        automatic_uploads: true,
        file_picker_types: 'image,file',
        file_picker_callback: function(cb, value, meta) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', meta.filetype === 'image' ? 'image/*' : '*/*');

            input.onchange = function() {
                setIsLoading(true);
                const file = this.files[0];
                const reader = new FileReader();

                reader.onload = function() {
                    const dataURL = reader.result;
                    fetch(JSON.parse(localStorage.getItem('linkCache')).backendUrl + '/email-attachment-upload', {
                        method: 'POST',
                        headers: {
                            Authorization: localStorage.getItem('token'),
                            'Content-Type': 'application/json',
                            Accept: 'application/json'
                        },
                        body: JSON.stringify({ dataURL, fileName: file.name })
                    })
                        .then(response => {
                            if (response.status === 413) {
                                throw new Error('File exceeds max size 50MB');
                            }
                            return response.json();
                        })
                        .then(data => {
                            cb(data.imageUrl, { alt: file.name, text: file.name });
                            setIsLoading(false);
                        })
                        .catch(e => {
                            setIsLoading(false);
                            if (e.message) {
                                toastr.error(e.message);
                            } else {
                                toastr.error('Error uploading file. Please try again.');
                            }
                        });
                };

                reader.readAsDataURL(file);
            };

            input.click();
        }
    };

    const handleInsertMergeTag = value => {
        if (editorRef?.current) {
            editorRef.current.insertContent(value);
        }
    };

    const editorHeight = window.innerHeight - 420;

    const validateEmails = emails => {
        if (!emails) return true;
        const emailArray = emails.split(',').map(e => e.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailArray.every(email => emailRegex.test(email));
    };

    const handleSend = () => {
        if (!validateEmails(emailTo)) {
            toastr.error('Invalid email in "Email to" field.');
            return;
        }
        if (!validateEmails(emailCc)) {
            toastr.error('Invalid email in "Email cc" field.');
            return;
        }
        onSend({ subject, content, emailTo, emailCc });
    };

    const customButtons = (
        <>
            <Button onClick={() => onClose()} className={globalStyles.cancelButton}>
                Cancel
            </Button>
            <Button
                disabled={!(subject && content) || isLoading}
                onClick={handleSend}
                className={`${globalStyles.baseButton} ${classes.actionButtonCompleteForm}`}
            >
                Send
            </Button>
        </>
    );

    const filterOptions = createFilterOptions({
        stringify: option => option.name
    });

    return (
        <>
            {isLoading && <LoadingScreen />}
            <Dialog
                id="send-invoice-email-modal"
                open
                fullWidth
                maxWidth={false}
                onClose={() => onClose()}
                disableEnforceFocus
                PaperProps={{
                    style: { maxWidth: '90%', width: '90%', height: '90vh' }
                }}
            >
                <DialogTitle style={{ padding: '12px 24px 0' }}>
                    <Typography style={{ fontSize: 18, fontWeight: 500, textAlign: 'center' }}>
                        Send invoice email
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        style={{ position: 'absolute', right: 4, top: 4 }}
                        onClick={() => onClose()}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '8px 24px' }}
                >
                    <Grid container spacing={2} style={{ marginBottom: 8, flexShrink: 0 }}>
                        <Grid item xs={6}>
                            <TextField
                                id="email-to"
                                label="Email to"
                                value={emailTo}
                                variant="outlined"
                                fullWidth
                                onChange={e => setEmailTo(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="email-cc"
                                label="Email cc"
                                value={emailCc}
                                variant="outlined"
                                fullWidth
                                onChange={e => setEmailCc(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id={'subject'}
                                label="Subject *"
                                value={subject}
                                variant="outlined"
                                fullWidth
                                onChange={e => setSubject(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                className={classes.templateAutocomplete}
                                options={[
                                    CREATE_NEW_TEMPLATE_OPTION,
                                    ...emailTemplates.map((t, i) => ({ ...t, index: i }))
                                ]}
                                getOptionLabel={option => option.name}
                                value={
                                    templateIndex === ''
                                        ? null
                                        : emailTemplates[templateIndex]
                                        ? { ...emailTemplates[templateIndex], index: templateIndex }
                                        : null
                                }
                                filterOptions={(options, state) => {
                                    const filtered = filterOptions(options, state);
                                    const createNewOption = options.find(o => o.isCreateNew);
                                    if (createNewOption && !filtered.includes(createNewOption)) {
                                        return [createNewOption, ...filtered];
                                    }
                                    return filtered;
                                }}
                                onChange={(e, newValue) => {
                                    if (!newValue) {
                                        setSelectedTemplate();
                                        setTemplateIndex('');
                                        return;
                                    }

                                    if (newValue.isCreateNew) {
                                        window.open(
                                            `${
                                                JSON.parse(localStorage.getItem('linkCache')).marketingUrl
                                            }/templates?createNew=true`,
                                            '_blank'
                                        );
                                        return;
                                    }

                                    const value = newValue.index;

                                    if (editorRef && editorRef.current) {
                                        let templateContent = emailTemplates[value].content;
                                        templateContent = templateContent.replace(
                                            '{ClientFirstName}',
                                            customer?.firstName || ''
                                        );
                                        templateContent = templateContent.replace(
                                            '{ClientSurname}',
                                            customer?.surname || ''
                                        );
                                        templateContent = templateContent.replace(
                                            '{InvoiceDetails}',
                                            invoiceDetails || ''
                                        );
                                        editorRef.current.value = templateContent;
                                        setContent(templateContent);
                                    }

                                    if (emailTemplates[value]?.subject) {
                                        setSubject(emailTemplates[value].subject);
                                    }

                                    setSelectedTemplate(emailTemplates[value]);
                                    setTemplateIndex(value);
                                }}
                                style={{ width: '100%' }}
                                renderInput={params => <TextField {...params} variant="outlined" label="Template" />}
                                renderOption={option => <Typography style={{ fontSize: 14 }}>{option.name}</Typography>}
                            />
                        </Grid>
                    </Grid>
                    <div style={{ display: 'flex', height: editorHeight }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <TinyMce
                                refEditor={editorRef}
                                value={content || ''}
                                onChange={val => setContent(val)}
                                init={{
                                    ...defaultConfig,
                                    plugins: ['lists', 'code', 'image', 'link', 'media', 'table'],
                                    height: '100%',
                                    toolbar:
                                        'undo redo | ' +
                                        'styles | ' +
                                        'bold italic strikethrough forecolor backcolor | ' +
                                        'link image media | ' +
                                        'table | ' +
                                        'indent align bullist numlist | ' +
                                        'code removeformat',
                                    table_toolbar:
                                        'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
                                    menubar: true,
                                    branding: false
                                }}
                            />
                        </div>
                        <div
                            style={{
                                width: 280,
                                minWidth: 280,
                                marginLeft: 16,
                                border: '1px solid rgba(0,0,0,0.12)',
                                borderRadius: '4px 4px 0 0',
                                overflowY: 'auto'
                            }}
                        >
                            <MergeTagsSidebar
                                customer={customer}
                                onInsert={handleInsertMergeTag}
                                initialInvoiceId={invoiceId}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>{customButtons}</DialogActions>
            </Dialog>
        </>
    );
};

SendInvoiceEmailModal.propTypes = {
    classes: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    customer: PropTypes.object,
    invoiceCode: PropTypes.string,
    invoiceId: PropTypes.string
};

export default withStyles(styles)(SendInvoiceEmailModal);
